import { ColDef, GridApi, GridReadyEvent, RowSelectionOptions } from '@ag-grid-community/core';
import { Component, ElementRef, Input, OnDestroy, OnInit } from '@angular/core';
import _ from 'lodash';
import { GridOptionsExt } from './ag-fns';
import { GRID_CONFIG } from './grid-config';
import { UserPrefsService } from './user-prefs.service';

@Component({
  selector: 'app-ag-grid',
  templateUrl: './ag-grid-wrapper.component.html',
  standalone: false,
})
export class AgGridWrapperComponent implements OnInit, OnDestroy {
  @Input() gridId?: string;
  @Input() gridOptions: GridOptionsExt;
  @Input() rowSelection?: RowSelectionOptions | 'single' | 'multiple';
  @Input() rowData?: any[];
  @Input() hasColSizeButtons = true;
  @Input() hasClearFiltersButton = true;
  @Input() hasFooter = true;

  // Expose GRID_CONFIG for template
  readonly gridConfig = GRID_CONFIG;

  constructor(
    public userPrefsService: UserPrefsService,
    private readonly el: ElementRef,
  ) {}

  get nativeElement(): HTMLElement {
    return this.el.nativeElement as HTMLElement;
  }

  get gridClass(): string {
    return this.gridId ? ` ${this.gridId}` : '';
  }

  ngOnInit() {
    this.rowSelection = this.gridOptions.rowSelection;
  }

  ngOnDestroy(): void {
    // if (this.gridId && window["__AG_GRID_TEST__"]?.[this.gridId]) {
    //   delete window["__AG_GRID_TEST__"][this.gridId];
    // }
  }

  onGridReady(event: GridReadyEvent) {
    // if (this.gridId) {
    //   window["__AG_GRID_TEST__"] = window["__AG_GRID_TEST__"] || {};
    //   window["__AG_GRID_TEST__"][this.gridId] = event.api;
    // }
    this.nativeElement.setAttribute('e2e-grid-ready', 'true');
  }

  onModelUpdated() {
    setTimeout(() => {
      let modelUpdatedCount = parseInt(this.nativeElement.getAttribute('e2e-model-updated-count') ?? '0');
      modelUpdatedCount = (modelUpdatedCount ?? 0) + 1;
      this.nativeElement.setAttribute('e2e-model-updated-count', '' + modelUpdatedCount);
    }, 100);
  }

  onFirstDataRendered() {
    // if (this.gridId) {
    //   window["__AG_GRID_TEST__"][this.gridId].firstDataRendered = true;
    // }
    this.nativeElement.setAttribute('e2e-first-data-rendered', 'true');
  }

  hasFilters() {
    const fm = this.gridOptions?.apiX?.getFilterModel();
    return !_.isEmpty(fm);
  }

  // // type guard
  // isColDef(toBeDetermined: ColDef | ColGroupDef): toBeDetermined is ColDef {
  //   return ((toBeDetermined as ColGroupDef).children) ? false : true;
  // }

  clearAllFilters() {
    this.gridOptions?.apiX?.setFilterModel(null);
    this.gridOptions?.apiX?.deselectAll();
  }
}

// An accessor to interact with AG-Grid from Playwright tests.
class AgGridAccessor {
  getApiRows(gridId: string): any[] {
    const api = this.getGridApi(gridId);
    const rows: any[] = [];
    api.forEachNode((node) => rows.push(node.data));
    return rows;
  }

  getDomRows(gridId: string): any[] {
    const rows: any[][] = [];
    document.querySelectorAll(`.${gridId} .ag-row`).forEach((r) => {
      const row: any[] = [];
      r.childNodes.forEach((cell: any) => {
        row.push(cell.innerText);
      });
      rows.push(row);
    });

    return rows;
  }

  getApiColumns(gridId: string): ColDef[] {
    const api = this.getGridApi(gridId);
    const columns: ColDef[] = [];
    api.getColumnDefs()?.forEach((col) => columns.push(col));
    return columns;
  }

  gridReady(gridId: string): boolean {
    return !!window['__AG_GRID_TEST__']?.[gridId];
  }

  gridFirstDataRendered(gridId: string): boolean {
    return !!window['__AG_GRID_TEST__']?.[gridId]?.firstDataRendered;
  }

  gridModelUpdatedCount(gridId: string): number {
    return window['__AG_GRID_TEST__']?.[gridId]?.modelUpdatedCount ?? 0;
  }

  getCellValue(gridId: string, rowIndex: number, field: string): any {
    const rows = this.getApiRows(gridId);
    const row = rows[rowIndex];
    if (!row) {
      return undefined;
    }

    return row[field];
  }

  getApiColumnValues(gridId: string, column: string): any[] {
    const rows = this.getApiRows(gridId);
    return rows.map((row) => row[column]);
  }

  getDomColumnValues(gridId: string, column: number | string): any[] {
    if (typeof column === 'number') {
      const rows = this.getDomRows(gridId);
      return rows.map((row) => row[column]);
    }

    const columnValues: any[] = [];
    document.querySelectorAll(`.${gridId} .ag-cell[col-id="${column}"]`).forEach((cell: any) => {
      columnValues.push(cell.innerText);
    });
    return columnValues;
  }

  // Checks if grid data has changed and if so waits for a tick to let Angular update the DOM.
  hasChanged(gridId: string, column: number | string, originalValues: any[]): boolean {
    const currentValues = this.getDomColumnValues(gridId, column);
    console.log(`Comparing column "${column}" values:`, currentValues, originalValues);
    if (currentValues.length !== originalValues.length) {
      return true;
    }

    for (let i = 0; i < currentValues.length; i++) {
      if (currentValues[i] !== originalValues[i]) {
        return true;
      }
    }
    return false;
  }

  getDomRowCount(gridId: string): number {
    const rows = this.getDomRows(gridId);
    return rows.length;
  }

  getApiRowCount(gridId: string): number {
    const rows = this.getApiRows(gridId);
    return rows.length;
  }

  hasData(gridId: string, data: Record<string, unknown>): boolean {
    const rows = this.getApiRows(gridId);
    return rows.some((row) => {
      return Object.keys(data).every((key) => row[key] === data[key]);
    });
  }

  ensureColumnVisible(gridId: string, column: string): void {
    const api = this.getGridApi(gridId);
    api.ensureColumnVisible(column);
  }

  private getGridApi(gridId: string): GridApi {
    const api: GridApi = window['__AG_GRID_TEST__']?.[gridId];
    if (!api) {
      throw new Error(`Grid API not found for gridId: ${gridId}`);
    }
    return api;
  }
}

window['agGrid'] = new AgGridAccessor();
